import { useCallback } from "react";
import { MOCK_INTEGRATIONS } from "../pages/Accounting/IntegrationTransactions/integrationMockData";
import type { Integration } from "../pages/Accounting/IntegrationTransactions/integrationTypes";
import type { QueryResult } from "./types";

/**
 * Returns the catalog of integrations the user can connect to Novala
 * (payment processors, e-commerce platforms, marketplaces, POS,
 * billing).
 *
 * Each integration's status reflects the current signed-in user's
 * connection state: "connected" (actively syncing), "available" (not
 * yet connected), or "coming_soon" (planned but not released).
 *
 * Model A (Novala's shared-list architecture): one connection, many
 * surfaces. Stripe connected here appears in Sales & Get Paid and
 * Overview automatically. No duplicate connections anywhere.
 *
 * Mock behavior (today):
 *   - Returns MOCK_INTEGRATIONS with Stripe and Shopify pre-connected
 *     so stakeholders can see the Connected + Available split without
 *     switching users.
 *
 * Backend behavior (future):
 *   - Replace with useQuery(["integrations"], () =>
 *     fetch("/api/integrations"))
 *   - Backend returns the full catalog with per-user status.
 *   - When a user completes an OAuth flow, the API flips that
 *     integration's status to "connected" and this hook refetches
 *     automatically (TanStack Query cache invalidation).
 *   - Components calling useIntegrations() do NOT change.
 *
 * Future cross-page use: Sales & Get Paid will call useIntegrations()
 * filtered to payment_processor + ecommerce + marketplace to show
 * which revenue sources are connected.
 */
export function useIntegrations(): QueryResult<Integration[]> {
  const refetch = useCallback(() => {
    // No-op in the mock implementation. The backend version uses
    // queryClient.invalidateQueries(["integrations"]) under the hood.
  }, []);

  return {
    data: MOCK_INTEGRATIONS,
    loading: false,
    error: null,
    refetch,
  };
}